import React from 'react';
import Head from 'next/head';
import ContentLoader from 'react-content-loader';
import { formatDistanceToNow } from 'date-fns';
import Navbar from '../../components/NavBar';
import Footer from '../../components/Footer';
import EarnProgramBox from '../../components/EarnProgram';
import styles from '../../css/ReleaseAll.module.css'; // Ensure you have a corresponding CSS file
import Link from 'next/link';
import Image from 'next/image';

const categories = [
  { name: 'Coins', link: '/release/Coins' },
  { name: 'Altcoins', link: '/release/Altcoins' },
  { name: 'MemeCoins', link: '/release/MemeCoins' },
  { name: 'NewCoins', link: '/release/NewCoins' },
  { name: 'Bitcoin', link: '/release/Bitcoin' },
  { name: 'Solana', link: '/release/Solana' },
  { name: 'Ethereum', link: '/release/Ethereum' },
];

const PumpCoinPage = ({ pumpedCoinNews, category, loading }) => {
  const headingMap = {
    coins: 'All New Released Coins',
    altcoins: 'New Released Altcoins',
    memecoins: 'New Released MemeCoins',
    newcoins: 'All New Released Coins',
    bitcoin: 'New Released Bitcoin Coins',
    solana: 'New Released Solana Coins',
    ethereum: 'New Released Ethereum Coins',
  };

  const headingText = headingMap[category.toLowerCase()] || 'New Released Coins';

  return (
    <div className={styles.pumpCoinPage}>
      <Head>
        <title>Newly Released Crypto Coins | Daily Coin Market</title>
        <meta name="description" content={`Explore the latest pumped news in the ${category || 'crypto'} category on DailyCoinMarket. Stay updated with the newest trends and analysis.`} />
        <meta name="keywords" content={`pumped news, ${category || 'crypto'}, cryptocurrency news, crypto updates, DailyCoinMarket, latest crypto trends`} />
        <link rel="canonical" href={`https://dailycoinmarket.com/release/${category || 'all'}`} />
        <meta property="og:title" content={`${category ? `${category} - Pumped Coin News` : 'Pumped Coin News'} | DailyCoinMarket`} />
        <meta property="og:description" content={`Explore the latest pumped news in the ${category || 'crypto'} category on DailyCoinMarket. Stay updated with the newest trends and analysis.`} />
        <meta property="og:image" content="https://dailycoinmarket.com/upload/default-image.jpg" />
        <meta property="og:url" content={`https://dailycoinmarket.com/release/${category || 'all'}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${category ? `${category} - Pumped Coin News` : 'Pumped Coin News'} | DailyCoinMarket`} />
        <meta name="twitter:description" content={`Explore the latest pumped news in the ${category || 'crypto'} category on DailyCoinMarket. Stay updated with the newest trends and analysis.`} />
        <meta name="twitter:image" content="https://dailycoinmarket.com/upload/default-image.jpg" />
      </Head>
      <Navbar />
      <EarnProgramBox />
      <h1 className={styles.title}>{headingText}</h1>
      <div className={styles.contentContainer}>
        <div className={styles.categoryNav}>
          {categories.map((cat, index) => (
            <Link key={index} href={cat.link} passHref>
             
                {cat.name === 'Coins' ? 'All Coins' : cat.name}
       
            </Link>
          ))}
        </div>
        <div className={styles.newsContainer}>
          {loading
            ? Array.from({ length: 6 }).map((_, index) => (
                <ContentLoader
                  key={index}
                  speed={2}
                  width={400}
                  height={300}
                  viewBox="0 0 400 300"
                  backgroundColor="#f3f3f3"
                  foregroundColor="#ecebeb"
                  className={styles.newsItem}
                >
                  <rect x="0" y="0" rx="5" ry="5" width="400" height="200" />
                  <rect x="0" y="210" rx="5" ry="5" width="300" height="20" />
                  <rect x="0" y="240" rx="5" ry="5" width="250" height="20" />
                </ContentLoader>
              ))
            : pumpedCoinNews.map((news, index) => (
                <div key={index} className={styles.newsItem}>
                  <Link href={`/CoinReleased/${news.slug}`} passHref>
  
                      <Image
                        src={news.featured_image[0]}
                        alt={news.title}
                        width={400} // Adjust width and height as needed
                        height={200}
                        className={styles.newsImage}
                      />
                      <h2 className={styles.newsTitle}>{news.title}</h2>


                  </Link>
                  <p className={styles.cryptoDate}>
                        {formatDistanceToNow(new Date(news.created_at), { addSuffix: true })}
                      </p>
                </div>
              ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export async function getStaticProps(context) {
  const category = context.params.category || 'all';
  
  try {
    const response = await fetch('https://dailycoinmarket.com/get-all-released-news');
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    const parsedData = data.map(news => ({
      ...news,
      featured_image: JSON.parse(news.featured_image),
    }));

    const filteredData = category.toLowerCase() !== 'coins'
      ? parsedData.filter(news => news.category.toLowerCase() === category.toLowerCase())
      : parsedData;

    return {
      props: {
        pumpedCoinNews: filteredData,
        category,
        loading: false,
      },
      revalidate: 60, // Revalidate every 60 seconds
    };
  } catch (error) {
    console.error('Error fetching pumped news:', error);
    return {
      props: {
        pumpedCoinNews: [],
        category,
        loading: true,
      },
      revalidate: 60,
    };
  }
}

export async function getStaticPaths() {
  const paths = categories.map((category) => ({
    params: { category: category.name.toLowerCase() },
  }));

  return { paths, fallback: 'blocking' };
}

export default PumpCoinPage;
